// ────────────────────────────────────────────────────────────
// DEAL DESK — party identity + corpus client
// ────────────────────────────────────────────────────────────
// The shared identity layer: DD chain parties resolve to
// mi.parties IDs (alias-aware, server-side), and a resolved party's
// docs come from the corpus MI already indexes. Reads and joins
// only — nothing here repulls or processes documents.

import { authHeaders } from '../../lib/authHeaders';
import type { PartyResolution, PartyCorpusDoc, DealRecord, DealListRow } from '../types';

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const r = await fetch(path, {
    ...init,
    credentials: 'include',
    headers: { ...authHeaders(), 'Content-Type': 'application/json', ...(init?.headers || {}) },
  });
  if (!r.ok) {
    let msg = await r.text();
    try { msg = JSON.parse(msg).error ?? msg; } catch { /* raw text */ }
    throw new Error(`API ${r.status}: ${msg}`);
  }
  return r.json() as Promise<T>;
}

/**
 * Resolve chain party names (with alias variants) → mi.parties IDs.
 * `trs` enables tract corroboration: trigram matches with instruments
 * in the tract (or adjacent sections) grade up to "probable".
 */
export function resolveParties(
  parties: Array<{ name: string; aliases: string[] }>,
  trs?: string,
): Promise<{ resolutions: PartyResolution[] }> {
  return api('/api/deal-desk/party-resolve', {
    method: 'POST',
    body: JSON.stringify({ parties, trs }),
  });
}

/** MI-indexed docs for a resolved party, both roles. Read/join only. */
export function partyCorpusDocs(partyId: string): Promise<{ documents: PartyCorpusDoc[] }> {
  return api(`/api/deal-desk/parties/${encodeURIComponent(partyId)}/docs`);
}

/**
 * Explicit human action: record the alias link for a graded match.
 * The ONLY path by which permuted-firm/probable evidence joins the
 * append-only identity record.
 */
export function confirmIdentity(input: {
  partyId: string; name: string; aliases: string[];
}): Promise<{ ok: boolean; displayName: string; inserted: number; alreadyRecorded: boolean }> {
  return api('/api/deal-desk/party-confirm', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/** Explicit action: create a thin deal record for a tract party. */
export function convertToDeal(input: {
  trs: string; county: string; subjectParty: string;
}): Promise<{ deal: { id: string; ref: string }; duplicate: boolean }> {
  return api('/api/deal-desk/deals', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/** Live thin-deal records for a tract (merged with fixture deals by the page). */
export function listDealsForTract(trs: string): Promise<{ deals: DealRecord[] }> {
  return api(`/api/deal-desk/deals?trs=${encodeURIComponent(trs)}`);
}

/** Live titled-deal rows for the deal list (merged with fixture rows by the page). */
export function listLiveTitledDeals(): Promise<{ deals: Array<DealRecord & { nmaEstimate: number | null }> }> {
  return api('/api/deal-desk/deals');
}

export type { DealListRow };
